import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, PenLine, Leaf, Quote, Moon, Sparkle } from 'lucide-react';

const PACKS = [
  {
    name: 'Autumn Nights',
    icon: Leaf,
    description: 'Cozy, melancholy, golden-hour stories',
    prompts: [
      'The last leaf refused to fall, and she knew exactly why.',
      'Every October, the lighthouse keeper sets a second place at dinner.',
      'The bonfire smoke spelled out a name no one had spoken in years.',
      'He raked the leaves into the shape of the person he missed.',
    ],
  },
  {
    name: 'First Lines',
    icon: Quote,
    description: 'Openers that demand a second sentence',
    prompts: [
      'Toby Paxton hated Wednesday afternoons, and this one was already worse than most.',
      'Maureen had kept the receipt for eleven years, folded inside her passport.',
      'The estate agent said the house had \u201Ccharacter,\u201D which turned out to mean a locked room.',
      'Dad rang at 6:40 a.m., which he only ever did when someone had died or the boiler had.',
    ],
  },
  {
    name: 'Midnight Mysteries',
    icon: Moon,
    description: 'Secrets, shadows, and things half-seen',
    prompts: [
      'The train stops at a station that is not on any map.',
      'Every night at 3:07, the phone rings once and stops.',
      'She found a photograph of her house, taken from inside.',
      'The town remembers a festival that never happened.',
    ],
  },
  {
    name: 'Small Wonders',
    icon: Sparkle,
    description: 'Tiny moments, enormous feelings',
    prompts: [
      'The bus driver waits an extra thirty seconds every morning for a woman who stopped catching it in March.',
      'Priya finds her late mother\u2019s handwriting on a shopping list tucked in a library book.',
      'Two neighbours who have never spoken end up sharing an umbrella at the school gate.',
      'A retired teacher gets a wedding invitation from a pupil he was sure had forgotten him.',
    ],
  },
];

const PromptCollections = ({ onUsePrompt }) => {
  const [openPack, setOpenPack] = useState(null);

  return (
    <div className="mt-8" data-testid="prompt-collections">
      <h3 className="font-medium text-gray-900 mb-1">Prompt Collections</h3>
      <p className="text-sm text-gray-500 mb-4">Curated packs, ready when you are — no generation needed.</p>
      <div className="space-y-3">
        {PACKS.map((pack) => {
          const Icon = pack.icon;
          const isOpen = openPack === pack.name;
          return (
            <Card key={pack.name} className="border-amber-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenPack(isOpen ? null : pack.name)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-amber-50/60 transition-colors"
                data-testid={`pack-toggle-${pack.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-center space-x-3">
                  <div className="bg-amber-100 p-2 rounded-full">
                    <Icon className="h-4 w-4 text-amber-800" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{pack.name}</p>
                    <p className="text-xs text-gray-500">{pack.description}</p>
                  </div>
                </div>
                <ChevronDown className={`h-4 w-4 text-amber-700 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <CardContent className="pt-0 pb-4 space-y-2" data-testid={`pack-prompts-${pack.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  {pack.prompts.map((prompt, i) => (
                    <div key={prompt} className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
                      <p className="text-sm text-amber-900 italic">&ldquo;{prompt}&rdquo;</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onUsePrompt(prompt)}
                        className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
                        data-testid={`use-pack-prompt-${pack.name.toLowerCase().replace(/\s+/g, '-')}-${i}`}
                      >
                        <PenLine className="mr-1 h-3 w-3" />
                        Write
                      </Button>
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PromptCollections;
